import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Briefcase,
  Wrench,
  DollarSign,
  Settings,
  LogOut,
  Search,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Menu,
  X,
  Globe,
  TrendingUp,
  Shield,
  Link as LinkIcon,
  ChevronRight,
  BarChart3,
  FileText,
  Activity,
  Loader2,
  Gift,
  ExternalLink,
  Wallet,
  Send,
  AlertTriangle,
  Calendar,
  RefreshCw,
  Sparkles,
  Mail,
} from 'lucide-react';

import CompanyApplications from './CompanyApplications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compliance Alerts Section Component
function ComplianceAlertsSection({ buildings }) {
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [alertSummary, setAlertSummary] = useState({ total: 0, expired: 0, expiring_soon: 0, missing: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    fetchComplianceAlerts();
  }, []);

  const fetchComplianceAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const response = await fetch(`${API_URL}/api/building-records/alerts/all?days=60`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
        setAlertSummary(data.summary || { total: 0, expired: 0, expiring_soon: 0, missing: 0 });
      }
    } catch (error) {
      console.error('Error fetching compliance alerts:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'expired': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'expiring_soon': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'attention': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'missing': return 'bg-slate-600/50 text-slate-300 border-slate-500/30';
      default: return 'bg-slate-600 text-slate-300';
    }
  };

  if (loadingAlerts) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" />
          <p className="text-slate-400 mt-2">Loading compliance alerts...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-slate-300" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{alertSummary.total}</p>
                <p className="text-xs text-slate-400">Total Alerts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-400">{alertSummary.expired}</p>
                <p className="text-xs text-slate-400">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{alertSummary.expiring_soon}</p>
                <p className="text-xs text-slate-400">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-300">{alertSummary.missing}</p>
                <p className="text-xs text-slate-400">Missing Records</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            Compliance Alerts (Next 60 Days)
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchComplianceAlerts} className="border-slate-600 text-slate-300 hover:text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">All Clear!</p>
              <p className="text-slate-500 text-sm">No compliance alerts found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Building</TableHead>
                  <TableHead className="text-slate-300">Compliance Type</TableHead>
                  <TableHead className="text-slate-300">Expiry Date</TableHead>
                  <TableHead className="text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert, idx) => (
                  <TableRow key={idx} className="border-slate-700">
                    <TableCell className="text-white font-medium">
                      {alert.building_name || 'Unknown'}
                      {alert.building_address && (
                        <p className="text-xs text-slate-500">{alert.building_address}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {alert.name || alert.compliance_type}
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {alert.is_missing ? (
                        <span className="text-slate-500">Not on file</span>
                      ) : (
                        formatDate(alert.expiry_date)
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(alert.status_info?.status)}>
                        {alert.status_info?.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/20"
                        onClick={() => navigate(`/platform/building/${alert.hoa_id}/record`)}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Company Invitations Analytics Widget
function CompanyInvitationsAnalytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const r = await fetch(`${API_URL}/api/company-invitations/_analytics`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) setStats(await r.json());
      } catch (e) {
        // silently ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6 text-slate-400">Loading invitation analytics…</CardContent>
      </Card>
    );
  }
  if (!stats) return null;

  const tiles = [
    { label: 'Total Invitations', value: stats.total_invitations, colour: 'text-white' },
    { label: 'Pending', value: stats.pending, colour: 'text-amber-400' },
    { label: 'Accepted', value: stats.accepted, colour: 'text-emerald-400' },
    { label: 'Declined', value: stats.declined, colour: 'text-red-400' },
    { label: 'Expired', value: stats.expired, colour: 'text-slate-400' },
    { label: 'Conversion', value: `${stats.conversion_rate_pct}%`, colour: 'text-indigo-400' },
  ];

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="invitation-analytics">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-white flex items-center gap-2">
          <Send className="w-5 h-5 text-indigo-400" />
          Building → Company Invitations
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
          onClick={() => navigate('/agent/building-invitations')}
          data-testid="view-all-invitations-btn"
        >
          View All
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiles.map((t) => (
            <div key={t.label} className="bg-slate-700/40 rounded-lg p-3" data-testid={`tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`text-2xl font-bold ${t.colour}`}>{t.value}</div>
              <div className="text-xs text-slate-400 mt-1">{t.label}</div>
            </div>
          ))}
        </div>
        {stats.top_requested_companies?.length > 0 && (
          <div className="mt-5">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Top Requested Companies</p>
            <div className="space-y-1">
              {stats.top_requested_companies.slice(0, 5).map((c) => (
                <div key={c.company_id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-slate-700/30">
                  <span className="text-slate-200 truncate">{c.company_name}</span>
                  <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30">{c.count}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const sidebarItems = [
  { icon: BarChart3, label: 'Dashboard', tab: 'overview' },
  { icon: Building2, label: 'Buildings', tab: 'buildings', isLink: true, path: '/platform/buildings' },
  { icon: Briefcase, label: 'Managing Agents', tab: 'agents' },
  { icon: Gift, label: 'Partner Program', tab: 'partners', isLink: true, path: '/platform/partners' },
  { icon: Wallet, label: 'Payment Management', tab: 'payments', isLink: true, path: '/platform/payments' },
  { icon: Users, label: 'Users', tab: 'users' },
  { icon: Shield, label: 'User Management', tab: 'user-mgmt', isLink: true, path: '/platform/user-management' },
  { icon: Send, label: 'Communications', tab: 'comms', isLink: true, path: '/platform/communications' },
  { icon: Sparkles, label: 'Bo-Dy Knowledge', tab: 'bo-dy', isLink: true, path: '/platform/bo-dy/knowledge' },
  { icon: DollarSign, label: 'Revenue', tab: 'revenue' },
  { icon: Activity, label: 'Audit Logs', tab: 'logs', isLink: true, path: '/platform/audit-log' },
  { icon: Shield, label: 'Compliance Alerts', tab: 'compliance', isLink: true, path: '/platform/compliance-alerts' },
  { icon: Shield, label: 'Consent Log', tab: 'consent', isLink: true, path: '/platform/consent-log' },
  { icon: Mail, label: 'Nominated Agents', tab: 'agent-nominations', isLink: true, path: '/platform/agent-nominations' },
  { icon: Settings, label: 'Settings', tab: 'settings' },
];

export default function PlatformOwnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data
  const [dashboardData, setDashboardData] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [platformSettings, setPlatformSettings] = useState({
    platform_name: 'BodyCorp365',
    support_email: 'support@bodycorp365.com',
    default_currency: 'ZAR',
    commission_rate: 10,
    trial_days: 14,
    enable_signups: true,
    maintenance_mode: false,
  });
  
  // Dialogs
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignData, setAssignData] = useState({ company_id: '', hoa_id: '' });
  const [assignLoading, setAssignLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const dashRes = await fetch(`${API_URL}/api/platform/dashboard`, {
        credentials: 'include'
      });
      if (dashRes.ok) {
        setDashboardData(await dashRes.json());
      }
      
      // Fetch buildings
      const buildRes = await fetch(`${API_URL}/api/platform/buildings`, {
        credentials: 'include'
      });
      if (buildRes.ok) {
        setBuildings(await buildRes.json());
      }
      
      // Fetch companies
      const compRes = await fetch(`${API_URL}/api/managing-agents/companies`, {
        credentials: 'include'
      });
      if (compRes.ok) {
        setCompanies(await compRes.json());
      }
      
      // Fetch users
      const usersRes = await fetch(`${API_URL}/api/platform/users?limit=50`, {
        credentials: 'include'
      });
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }
      
      // Fetch audit logs
      const logsRes = await fetch(`${API_URL}/api/platform/audit-logs?limit=50`, {
        credentials: 'include'
      });
      if (logsRes.ok) {
        setAuditLogs(await logsRes.json());
      }
      
      // Fetch revenue data
      const revenueRes = await fetch(`${API_URL}/api/platform/revenue`, {
        credentials: 'include'
      });
      if (revenueRes.ok) {
        setRevenueData(await revenueRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Use demo data
      setDashboardData({
        buildings: { total: 5, active: 4, new_this_week: 1 },
        users: { total: 120, residents: 100, trustees: 15, agents: 5, new_this_week: 8 },
        managing_agents: { total_companies: 3, active: 2, pending_approval: 1 },
        tickets: { total: 45, open: 12, new_this_week: 6 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCompany = async (companyId) => {
    try {
      const response = await fetch(`${API_URL}/api/managing-agents/company/${companyId}/approve`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Company approved successfully');
        fetchDashboardData();
      } else {
        throw new Error('Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve company');
    }
  };

  const handleAssignBuilding = async () => {
    if (!assignData.company_id || !assignData.hoa_id) {
      toast.error('Please select both company and building');
      return;
    }

    setAssignLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/managing-agents/assign-building`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_id: assignData.company_id,
          hoa_id: assignData.hoa_id,
          permissions: ['view', 'tickets', 'documents', 'notices', 'financials']
        })
      });
      
      if (response.ok) {
        toast.success('Building assigned successfully');
        setIsAssignOpen(false);
        setAssignData({ company_id: '', hoa_id: '' });
        fetchDashboardData();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to assign');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <Link to="/platform" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="BodyCorp365" className="w-10 h-10" />
              <div>
                <p className="font-bold text-white">BodyCorp365</p>
                <p className="text-xs text-amber-400">Platform Owner</p>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.tab}>
                  <button
                    onClick={() => item.isLink ? navigate(item.path) : setActiveTab(item.tab)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      activeTab === item.tab
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {item.isLink && <ExternalLink className="w-3 h-3 ml-auto opacity-50" />}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'P'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Platform Owner'}</p>
                <p className="text-xs text-amber-400 truncate">Platform Owner</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-slate-800 border-b border-slate-700">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" className="text-white" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-bold text-white">Platform Dashboard</span>
          <div className="w-10" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white">Platform Dashboard</h1>
              <p className="text-slate-400 mt-1">System-wide overview and management</p>
            </div>
            <Button 
              onClick={() => setIsAssignOpen(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="assign-building-btn"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Assign Building
            </Button>
          </div>

          {/* Stats Grid */}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[1, 2, 3, 4].map(i => (
                <Card key={i} className="bg-slate-800 border-slate-700 animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-16 bg-slate-700 rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Buildings</p>
                      <p className="text-3xl font-bold text-white">{dashboardData?.buildings?.total || 0}</p>
                      <p className="text-emerald-400 text-xs mt-1">+{dashboardData?.buildings?.new_this_week || 0} this week</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-indigo-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Total Users</p>
                      <p className="text-3xl font-bold text-white">{dashboardData?.users?.total || 0}</p>
                      <p className="text-emerald-400 text-xs mt-1">+{dashboardData?.users?.new_this_week || 0} this week</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Managing Agents</p>
                      <p className="text-3xl font-bold text-white">{dashboardData?.managing_agents?.total_companies || 0}</p>
                      {dashboardData?.managing_agents?.pending_approval > 0 && (
                        <p className="text-amber-400 text-xs mt-1">{dashboardData.managing_agents.pending_approval} pending</p>
                      )}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Open Tickets</p>
                      <p className="text-3xl font-bold text-white">{dashboardData?.tickets?.open || 0}</p>
                      <p className="text-slate-500 text-xs mt-1">{dashboardData?.tickets?.total || 0} total</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Wrench className="w-6 h-6 text-amber-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-slate-800 border border-slate-700">
              <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                Overview
              </TabsTrigger>
              <TabsTrigger value="agents" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                Managing Agents
              </TabsTrigger>
              <TabsTrigger value="company-applications" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400" data-testid="company-applications-tab">
                Company Applications
              </TabsTrigger>
              <TabsTrigger value="buildings" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">
                Buildings
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <CompanyInvitationsAnalytics />
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Pending Approvals */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-400" />
                      Pending Approvals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {companies.filter(c => c.status === 'pending_approval').length === 0 ? (
                      <p className="text-slate-400 text-center py-4">No pending approvals</p>
                    ) : (
                      <div className="space-y-3">
                        {companies.filter(c => c.status === 'pending_approval').map(company => (
                          <div key={company.company_id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                            <div>
                              <p className="font-medium text-white">{company.company_name}</p>
                              <p className="text-sm text-slate-400">{company.contact_email}</p>
                            </div>
                            <Button size="sm" onClick={() => handleApproveCompany(company.company_id)} className="bg-emerald-500 hover:bg-emerald-600">
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Buildings */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-indigo-400" />
                      Recent Buildings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {buildings.slice(0, 5).map(building => (
                      <div 
                        key={building.hoa_id} 
                        className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0 hover:bg-slate-700/30 px-2 rounded cursor-pointer transition-colors"
                        onClick={() => navigate(`/platform/building/${building.hoa_id}/record`)}
                        data-testid={`building-row-${building.hoa_id}`}
                      >
                        <div>
                          <p className="font-medium text-white">{building.name}</p>
                          <p className="text-sm text-slate-400">{building.stats?.units || building.total_units || 0} units • {building.country}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {building.managing_agent ? (
                            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                              {building.managing_agent}
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-600 text-slate-300">Unassigned</Badge>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Managing Agents Tab */}
            <TabsContent value="agents">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-400">Company</TableHead>
                        <TableHead className="text-slate-400">Contact</TableHead>
                        <TableHead className="text-slate-400">Buildings</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companies.map(company => (
                        <TableRow key={company.company_id} className="border-slate-700 hover:bg-slate-700/50">
                          <TableCell className="text-white font-medium">{company.company_name}</TableCell>
                          <TableCell className="text-slate-300">{company.contact_email}</TableCell>
                          <TableCell className="text-slate-300">{company.building_count || 0}</TableCell>
                          <TableCell>
                            <Badge className={
                              company.status === 'active' 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }>
                              {company.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {company.status === 'pending_approval' && (
                              <Button size="sm" onClick={() => handleApproveCompany(company.company_id)} className="bg-emerald-500 hover:bg-emerald-600">
                                Approve
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Company Applications Tab */}
            <TabsContent value="company-applications">
              <CompanyApplications />
            </TabsContent>

            {/* Buildings Tab */}
            <TabsContent value="buildings">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-400">Building</TableHead>
                        <TableHead className="text-slate-400">Country</TableHead>
                        <TableHead className="text-slate-400">Units</TableHead>
                        <TableHead className="text-slate-400">Managing Agent</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {buildings.map(building => (
                        <TableRow key={building.hoa_id} className="border-slate-700 hover:bg-slate-700/50">
                          <TableCell className="text-white font-medium">{building.name}</TableCell>
                          <TableCell className="text-slate-300">{building.country}</TableCell>
                          <TableCell className="text-slate-300">{building.stats?.units || building.total_units || 0}</TableCell>
                          <TableCell>
                            {building.managing_agent ? (
                              <span className="text-purple-400">{building.managing_agent}</span>
                            ) : (
                              <span className="text-slate-500">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              building.subscription_status === 'active' 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-slate-600 text-slate-300'
                            }>
                              {building.subscription_status || 'active'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/20"
                              onClick={() => navigate(`/platform/building/${building.hoa_id}/record`)}
                              data-testid={`view-record-${building.hoa_id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              View Record
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">All Platform Users</CardTitle>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-64 bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Name</TableHead>
                        <TableHead className="text-slate-300">Email</TableHead>
                        <TableHead className="text-slate-300">Role</TableHead>
                        <TableHead className="text-slate-300">Building</TableHead>
                        <TableHead className="text-slate-300">Status</TableHead>
                        <TableHead className="text-slate-300">Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users
                        .filter(u => 
                          !searchQuery || 
                          u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          u.email?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map((u, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell className="text-white">{u.name || 'Unknown'}</TableCell>
                          <TableCell className="text-slate-300">{u.email}</TableCell>
                          <TableCell>
                            <Badge className={
                              u.role === 'platform_owner' ? 'bg-purple-500/20 text-purple-400' :
                              u.role === 'managing_agent' ? 'bg-blue-500/20 text-blue-400' :
                              u.role === 'trustee' ? 'bg-amber-500/20 text-amber-400' :
                              u.role === 'property_manager' ? 'bg-green-500/20 text-green-400' :
                              'bg-slate-600 text-slate-300'
                            }>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{u.hoa_name || u.hoa_id || '-'}</TableCell>
                          <TableCell>
                            <Badge className={u.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                              {u.status || 'active'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-400">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {users.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                            No users found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Revenue Tab */}
            <TabsContent value="revenue">
              <div className="space-y-6">
                {/* Revenue Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                      <p className="text-slate-400 text-sm">Total Revenue (MTD)</p>
                      <p className="text-2xl font-bold text-emerald-400">R {(revenueData?.monthly_revenue || 15750).toLocaleString()}</p>
                      <p className="text-xs text-emerald-500">+12% from last month</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                      <p className="text-slate-400 text-sm">Active Subscriptions</p>
                      <p className="text-2xl font-bold text-white">{revenueData?.active_subscriptions || 24}</p>
                      <p className="text-xs text-slate-400">{revenueData?.units_subscribed || 156} units</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                      <p className="text-slate-400 text-sm">Avg Revenue/Building</p>
                      <p className="text-2xl font-bold text-white">R {(revenueData?.avg_per_building || 656).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">Per month</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                      <p className="text-slate-400 text-sm">Agent Commissions</p>
                      <p className="text-2xl font-bold text-amber-400">R {(revenueData?.agent_commissions || 1575).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">10% of subscriptions</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue by Building */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Revenue by Building</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-300">Building</TableHead>
                          <TableHead className="text-slate-300">Units</TableHead>
                          <TableHead className="text-slate-300">Rate/Unit</TableHead>
                          <TableHead className="text-slate-300">Monthly Revenue</TableHead>
                          <TableHead className="text-slate-300">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {buildings.map((b, idx) => (
                          <TableRow key={idx} className="border-slate-700">
                            <TableCell className="text-white">{b.name}</TableCell>
                            <TableCell className="text-slate-300">{b.total_units || 24}</TableCell>
                            <TableCell className="text-slate-300">R35/year</TableCell>
                            <TableCell className="text-emerald-400">R {((b.total_units || 24) * 35 / 12).toFixed(0)}</TableCell>
                            <TableCell>
                              <Badge className="bg-emerald-500/20 text-emerald-400">Paid</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Audit Logs Tab */}
            <TabsContent value="logs">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">Audit Logs</CardTitle>
                  <Badge className="bg-blue-500/20 text-blue-400">Last 50 events</Badge>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-300">Timestamp</TableHead>
                        <TableHead className="text-slate-300">User</TableHead>
                        <TableHead className="text-slate-300">Action</TableHead>
                        <TableHead className="text-slate-300">Resource</TableHead>
                        <TableHead className="text-slate-300">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(auditLogs.length > 0 ? auditLogs : [
                        { timestamp: new Date().toISOString(), user_email: 'platform@bodycorp365.com', action: 'LOGIN', resource: 'Auth', details: 'Successful login' },
                        { timestamp: new Date(Date.now() - 3600000).toISOString(), user_email: 'agent@propmanage.co.za', action: 'APPROVED', resource: 'Company', details: 'Company approval granted' },
                        { timestamp: new Date(Date.now() - 7200000).toISOString(), user_email: 'hoaadmin@bodycorp365.com', action: 'CREATE', resource: 'Ticket', details: 'Maintenance ticket created' },
                        { timestamp: new Date(Date.now() - 10800000).toISOString(), user_email: 'test@demo.com', action: 'UPDATE', resource: 'Profile', details: 'Profile updated' },
                        { timestamp: new Date(Date.now() - 14400000).toISOString(), user_email: 'platform@bodycorp365.com', action: 'ASSIGN', resource: 'Building', details: 'Building assigned to agent' },
                      ]).map((log, idx) => (
                        <TableRow key={idx} className="border-slate-700">
                          <TableCell className="text-slate-400 text-sm">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-slate-300">{log.user_email}</TableCell>
                          <TableCell>
                            <Badge className={
                              log.action === 'LOGIN' ? 'bg-blue-500/20 text-blue-400' :
                              log.action === 'CREATE' ? 'bg-emerald-500/20 text-emerald-400' :
                              log.action === 'UPDATE' ? 'bg-amber-500/20 text-amber-400' :
                              log.action === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                              'bg-slate-600 text-slate-300'
                            }>
                              {log.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white">{log.resource}</TableCell>
                          <TableCell className="text-slate-400 text-sm">{log.details}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Compliance Alerts Tab */}
            <TabsContent value="compliance">
              <ComplianceAlertsSection buildings={buildings} />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="space-y-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">General Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Platform Name</Label>
                        <Input
                          value={platformSettings.platform_name}
                          onChange={(e) => setPlatformSettings({...platformSettings, platform_name: e.target.value})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Support Email</Label>
                        <Input
                          value={platformSettings.support_email}
                          onChange={(e) => setPlatformSettings({...platformSettings, support_email: e.target.value})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Default Currency</Label>
                        <Select
                          value={platformSettings.default_currency}
                          onValueChange={(v) => setPlatformSettings({...platformSettings, default_currency: v})}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ZAR">ZAR (South Africa)</SelectItem>
                            <SelectItem value="ZAR">ZAR (South Africa)</SelectItem>
                            <SelectItem value="GBP">GBP (United Kingdom)</SelectItem>
                            <SelectItem value="EUR">EUR (Europe)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Trial Period (Days)</Label>
                        <Input
                          type="number"
                          value={platformSettings.trial_days}
                          onChange={(e) => setPlatformSettings({...platformSettings, trial_days: parseInt(e.target.value)})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Commission & Pricing</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Agent Commission Rate (%)</Label>
                        <Input
                          type="number"
                          value={platformSettings.commission_rate}
                          onChange={(e) => setPlatformSettings({...platformSettings, commission_rate: parseInt(e.target.value)})}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                        />
                        <p className="text-xs text-slate-400 mt-1">Commission paid to agents for referred buildings</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Platform Controls</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Enable New Signups</p>
                        <p className="text-slate-400 text-sm">Allow new buildings to register</p>
                      </div>
                      <Button
                        variant={platformSettings.enable_signups ? "default" : "outline"}
                        onClick={() => setPlatformSettings({...platformSettings, enable_signups: !platformSettings.enable_signups})}
                        className={platformSettings.enable_signups ? "bg-emerald-600" : ""}
                      >
                        {platformSettings.enable_signups ? 'Enabled' : 'Disabled'}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Maintenance Mode</p>
                        <p className="text-slate-400 text-sm">Put platform in maintenance mode</p>
                      </div>
                      <Button
                        variant={platformSettings.maintenance_mode ? "destructive" : "outline"}
                        onClick={() => setPlatformSettings({...platformSettings, maintenance_mode: !platformSettings.maintenance_mode})}
                      >
                        {platformSettings.maintenance_mode ? 'Active' : 'Inactive'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => toast.success('Settings saved successfully')}
                >
                  Save Settings
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Assign Building Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Assign Building to Managing Agent</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a building and managing agent company to create an assignment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Building</Label>
              <Select value={assignData.hoa_id} onValueChange={(v) => setAssignData({...assignData, hoa_id: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="assign-building-select">
                  <SelectValue placeholder="Select building" />
                </SelectTrigger>
                <SelectContent>
                  {buildings.map(b => (
                    <SelectItem key={b.hoa_id} value={b.hoa_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Managing Agent Company</Label>
              <Select value={assignData.company_id} onValueChange={(v) => setAssignData({...assignData, company_id: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="assign-company-select">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.filter(c => c.status === 'active').map(c => (
                    <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleAssignBuilding} disabled={assignLoading} className="bg-amber-500 hover:bg-amber-600">
              {assignLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LinkIcon className="w-4 h-4 mr-2" />}
              Assign Building
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
